import { RiLock2Line, RiShareForwardLine } from "@remixicon/react";
import React, { useState } from "react";
import {
    FieldTypedValueBaseInput,
    FormField,
    FormFieldDescriptorInput, FormFieldOptionInput, FormFieldReplacementInput, FormFieldType, FormFieldVisibility,
    FormType,
    GetFormDataDocument,
    GetRequestTypesDocument,
    useSaveFormFieldMutation,
} from "../../../../__generated__/api";
import { cloneWithoutTypename, createDeepEqual } from "../../../../api";
import Editor from "../../../../components/agent-v3/editor/Editor";
import Confirm from "../../../../components/confirm/Confirm";
import {
    ProxyProperty, useDependencyComparison, useFormBuilder,
} from "../../../../components/dirty-tracker/FormTracker";
import AdvancedSection from "../../../../components/expanding-section/AdvancedSection";
import FieldTypeValueDisplay, { DEFAULT_VALUE_REPLACEMENTS } from "../../../../components/form-field/FieldTypeValueDisplay";
import FieldSelector, { AGENT_TEXT, REQUESTER_TEXT } from "../../../../components/pickers/field-selector/FieldSelector";
import { CUSTOM_FIELD_PROPS, UserType } from "../../../../components/pickers/field-type/FieldType";
import Popover from "../../../../components/popover/Popover";
import Select from "../../../../components/select/Select";
import { useDrawerForm } from "../../../../components/theme/drawer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Icon from "../../../../components/theme/icon/Icon";
import Text from "../../../../components/theme/text/Text";
import Toggle from "../../../../components/theme/toggle/Toggle";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { DEEP_COMPARER } from "../base";
import Styles from "./CustomField.module.scss";
import OptionsDisplay from "./OptionsDisplay";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";

const CHOICE_FIELDS = new Set([
    FormFieldType.Dropdown,
    FormFieldType.Checkboxes,
    FormFieldType.MultiLevelDropdown,
    FormFieldType.MultiSelectDropdown,
]);

const VISBILITY_OPTIONS = [
    {
        label: (
            <>
                <Icon icon={<RiShareForwardLine />} variant="default" className="m3e-1" />
                {" "}
                Reusable
            </>
        ),
        value: FormFieldVisibility.Reusable,
    },
    {
        label: (
            <>
                <Icon icon={<RiLock2Line />} variant="default" className="m3e-1" />
                {" "}
                Private
            </>
        ),
        value: FormFieldVisibility.Private,
    },
];

export function CustomFieldV3({
    property,
    field,
    canOverride,
    isEdit,
    userGroupsIds,
    canAddUserGroups,
}: {
    property: ProxyProperty<FormFieldDescriptorInput>;
    field: ProxyProperty<FormField | undefined>;
    canOverride?: boolean;
    isEdit?: boolean;
    userGroupsIds?: string[];
    canAddUserGroups?: boolean;
}) {
    const [fieldTypeId, setFieldTypeId] = useState<FormFieldType | string | undefined>(
        property.value.fieldId
        || field.value?.type
        || (property.value.requestorText ? REQUESTER_TEXT : undefined)
        || (property.value.agentText ? AGENT_TEXT : undefined),
    );

    const descriptorProperties = property.useBuilder(builder => builder
        .property("name", item => item.name ?? "")
        .property("description", item => item.description ?? "")
        .property("isRequired", item => item.isRequired ?? field.value?.isRequired ?? false)
        .property("placeholder", item => item.placeholder ?? "")
        .object("defaultValue", item => item.defaultValue)
        .property("isRequiredForAgent", item => item.isRequiredForAgent ?? false)
        .property("fieldId", item => item.fieldId)
        .property("requestorText", item => item.requestorText)
        .property("agentText", item => item.agentText));

    const fieldProperties = field.useBuilder(builder => builder
        .property("id", item => item?.id)
        .property("name", item => item?.name ?? "")
        .property("type", item => item?.type ?? "")
        .array("userGroupIds", item => item?.userGroupIds as string[] ?? userGroupsIds)
        .property("description", item => item?.description ?? "")
        .property("placeholder", item => item?.placeholder ?? "")
        .array("options", item => item?.options as FormFieldOptionInput[] | undefined)
        .array("levels", item => item?.levels as string[] | undefined)
        .property("visibility", item => item?.visibility ?? FormFieldVisibility.Private));

    const fieldType = fieldProperties.type.value ?? fieldTypeId;
    const customFieldType = fieldType ? CUSTOM_FIELD_PROPS[fieldType as FormFieldType] : undefined;
    const isOverride = !isEdit
        && fieldTypeId?.startsWith("urn:fq:form-field")
        && field.value?.visibility === FormFieldVisibility.Reusable;
    const isEditing = isEdit || property.value?.fieldId;
    const isText = descriptorProperties.requestorText.value || descriptorProperties.agentText.value;

    const currentProperties = isOverride ? descriptorProperties : fieldProperties;
    const isOption = CHOICE_FIELDS.has(fieldProperties.type.value as FormFieldType);
    return (
        <div className={Styles["form-container"]}>
            <div className={Styles["form-body"]}>
                {!isEditing && (
                    <FormGroup
                        label="Field Type"
                        size="small"
                        groupClassName={Styles["field-type"]}
                    >
                        <FieldSelector
                            shouldShowAllFields={canOverride}
                            shouldGroupSystem={canOverride}
                            shouldAutoFocus
                            fieldDisplayUserType={UserType.END_USER}
                            shouldIncludeTextTypes={canOverride}
                            shouldIncludeIcons
                            value={fieldTypeId}
                            onChange={value => {
                                setFieldTypeId(value?.field.id);
                                if (value?.field.id.startsWith("urn:fq:form-field")) {
                                    descriptorProperties.fieldId.setValue(value?.field.id);
                                    field.setValue(value?.field);
                                } else {
                                    descriptorProperties.fieldId.setValue(undefined);
                                    const customField = CUSTOM_FIELD_PROPS[value?.field.id as FormFieldType];
                                    if (customField) {
                                        fieldProperties.type.setValue(value?.field.id as FormFieldType);
                                    }
                                }
                            }}
                        />
                    </FormGroup>
                )}
                {fieldTypeId && (

                    <div className={Styles.form}>
                        {isOverride && (
                            <Text as="h3" textVariant="body-1-semibold">
                                Overrides
                                <Popover
                                    hasArrow
                                    size="md"
                                    iconSize="sm"
                                    buttonClassName="ms-1"
                                    variant="tooltip"
                                >
                                    You have selected an existing field. This field already defines the type,
                                    name, description, etc. However, you are able to override most of the
                                    properties when added to this form.
                                </Popover>
                            </Text>
                        )}
                        {!isEditing && !isOverride && customFieldType && (
                            <Text as="h3" textVariant="body-1-semibold">
                                Add Field
                            </Text>
                        )}
                        {(customFieldType || isOverride || isEditing) && !isText && (
                            <>
                                <FormGroup
                                    type="text"
                                    label="Name"
                                    size="small"
                                    isRequired={!isEditing}
                                    placeholder={field.value?.name ?? ""}
                                    value={currentProperties.name.value}
                                    onChange={currentProperties.name.setValueFromInput}
                                />
                                {isEditing && !isOverride && (
                                    <FormGroup
                                        label="Type"
                                        size="small"
                                    >
                                        <FieldSelector
                                            fieldDisplayUserType={UserType.END_USER}
                                            shouldIncludeIcons
                                            value={fieldProperties.type?.value}
                                            onChange={value => {
                                                fieldProperties.type.setValue(value?.field.id);
                                            }}
                                        />
                                    </FormGroup>
                                )}
                                <FormGroup
                                    label="Description"
                                    size="small"
                                    isOptional
                                >
                                    <Editor
                                        value={currentProperties.description.value}
                                        onChange={currentProperties.description.setValue}
                                        size="medium"
                                        hasBorder
                                        placeholder={field.value?.description ?? ""}
                                    />

                                </FormGroup>
                                {canAddUserGroups && (
                                    <FormGroup
                                        label="Groups"
                                        size="small"
                                        isOptional
                                        helpDisplay="icon"
                                        helpText="Select the groups that will own and have access to this custom field."
                                    >
                                        <UserGroups
                                            isMulti
                                            value={fieldProperties.userGroupIds.value}
                                            onSelected={fieldProperties.userGroupIds.setValue}
                                        />

                                    </FormGroup>
                                )}
                                <FormGroup
                                    label="Required Field"
                                    size="small"
                                    helpDisplay="icon"
                                    helpText="If enabled, the user will be required to enter this field. Users will not
                                     be able to submit the request type until this field is provided."
                                >
                                    <div>
                                        <Toggle
                                            checked={descriptorProperties.isRequired.value}
                                            onChange={descriptorProperties.isRequired.setCheckedFromInput}
                                            text="If enabled, this field will be required."
                                        />
                                    </div>
                                </FormGroup>
                                <AdvancedSection title="Advanced Settings" bodyClassName={Styles["advanced-form"]}>
                                    <FormGroup
                                        type="text"
                                        label="Example text"
                                        size="small"
                                        isOptional
                                        placeholder={field.value?.placeholder ?? ""}
                                        value={currentProperties.placeholder.value}
                                        onChange={currentProperties.placeholder.setValueFromInput}
                                    />

                                    <FormGroup
                                        type="text"
                                        label="Default Value"
                                        size="small"
                                        isOptional
                                    >
                                        <FieldTypeValueDisplay
                                            inputClassName="form-control form-control-sm"
                                            shouldShowFieldName={false}
                                            shouldShowDescription={false}
                                            field={{
                                                ...field.value as FormField,
                                                type: customFieldType ? fieldTypeId : field.value?.type,
                                                isRequired: false,
                                                placeholder: `Enter ${currentProperties.name.value || "value"}`,
                                            } as FormField}
                                            value={descriptorProperties.defaultValue?.value?.value as FieldTypedValueBaseInput}
                                            onChange={(value, replacements) => descriptorProperties.defaultValue.setValue({
                                                value,
                                                replacements,
                                            })}
                                            replacements={
                                            descriptorProperties.defaultValue?.value?.replacements as FormFieldReplacementInput[]
                                            }
                                            replaceableInput={fieldTypeId === FormFieldType.Text
                                            || fieldTypeId === FormFieldType.LongText
                                                ? undefined : DEFAULT_VALUE_REPLACEMENTS}
                                        />
                                    </FormGroup>
                                    <FormGroup
                                        label="Required for Close"
                                        size="small"
                                        helpDisplay="icon"
                                        helpText="If enabled, the agent will be required to enter this field to close the ticket.
                                        Agents will not be able to close the ticket until this field is provided."
                                    >
                                        <div>
                                            <Toggle
                                                checked={descriptorProperties.isRequiredForAgent.value}
                                                onChange={descriptorProperties.isRequiredForAgent.setCheckedFromInput}
                                                text="If enabled, the agent will need to complete
                                             this field before the ticket will be closed."
                                            />
                                        </div>
                                    </FormGroup>
                                    {!isOverride && canOverride && (
                                        <FormGroup
                                            label="Visibility"
                                            size="small"
                                            helpDisplay="icon"
                                            helpText="If enabled, the agent will be required to enter
                                                this field to close the ticket. Agents will not be
                                                able to close the ticket until this field is provided."
                                        >
                                            <Select
                                                className="text-gray"
                                                placeholder=""
                                                onChange={value => fieldProperties.visibility.setValue(
                                            value.value as FormFieldVisibility,
                                                )}
                                                options={VISBILITY_OPTIONS}
                                                value={VISBILITY_OPTIONS.find(
                                                    option => option.value === fieldProperties.visibility.value,
                                                )}
                                            />
                                        </FormGroup>
                                    )}
                                </AdvancedSection>
                            </>
                        )}

                        {fieldTypeId === REQUESTER_TEXT && (
                            <FormGroup
                                label="Requester Text"
                                size="small"
                                helpDisplay="icon"
                                helpText="This field will be displayed to the requester when they are creating a ticket."
                            >
                                <Editor
                                    value={descriptorProperties.requestorText.value ?? ""}
                                    onChange={descriptorProperties.requestorText.setValue}
                                    size="medium"
                                    hasBorder
                                />
                            </FormGroup>
                        )}

                        {fieldTypeId === AGENT_TEXT && (
                            <FormGroup
                                label="Agent Text"
                                size="small"
                                helpDisplay="icon"
                                helpText="This field will be displayed to the agent in the ticketing system."
                            >
                                <Editor
                                    value={descriptorProperties.agentText.value ?? ""}
                                    onChange={descriptorProperties.agentText.setValue}
                                    size="medium"
                                    hasBorder
                                />
                            </FormGroup>
                        )}
                    </div>
                )}

            </div>
            {isOption && !isOverride && (

                <OptionsDisplay
                    title={customFieldType?.text}
                    options={fieldProperties.options.value}
                    levels={fieldProperties.levels.value}
                    onChange={options => {
                        fieldProperties.options.setValue(options.options);
                        fieldProperties.levels.setValue(options.levels);
                    }}
                    childrenLevels={customFieldType?.childLevels ?? 0}
                    level={0}
                />

            )}

        </div>
    );
}

CustomFieldV3.defaultProps = {
    canOverride: true,
    isEdit: false,
    userGroupsIds: undefined,
    canAddUserGroups: false,
};

export function useCustomFieldModal({
    onSave,
    userGroupsIds,
}: {
    onSave: (value: FormFieldDescriptorInput, field: FormField) => void;
    userGroupsIds?: string[]
}) {
    const [isShow, setShow] = useState(false);
    const { properties } = useFormBuilder(
        builder => builder
            .proxy<"value", FormFieldDescriptorInput | undefined>("value", () => undefined)
            .proxy<"field", FormField | undefined>("field", () => undefined),
        [],
    );

    const [saveFormField] = useSaveFormFieldMutation();

    const isOptions = properties.field.value?.visibility !== FormFieldVisibility.Reusable
                && properties.field.value?.type
                && CHOICE_FIELDS.has(properties.field.value.type);
    const element = (
        <Confirm
            title={properties.value?.value?.fieldId ? "Edit Field" : "Add Field"}
            shouldShow={isShow}
            confirmText="Save"
            size={isOptions ? "xl" : undefined}
            className={Styles.modal}
            onHide={() => {
                properties.value.setValue(undefined);
                setShow(false);
            }}
            onConfirm={() => {
                if (properties.value.value) {
                    let id = properties.value.value.fieldId;
                    LoggingHandler.instance.wrap(async () => {
                        let formField = properties.field.serialize() as FormField;

                        if (formField.type) {
                            delete formField.id;
                            const createdField = await saveFormField({
                                refetchQueries: [
                                    { query: GetFormDataDocument },
                                ],
                                variables: {
                                    id,
                                    formField: cloneWithoutTypename(formField),
                                },
                            });
                            formField = createdField.data?.saveFormField as FormField;
                            id = createdField.data?.saveFormField.id;
                        }

                        const serialized = properties.value.serialize() as FormFieldDescriptorInput;
                        onSave({
                            ...serialized,
                            fieldId: id,
                        }, formField as FormField);
                    });
                }
                properties.value.setValue(undefined);
                setShow(false);
            }}
        >
            <CustomFieldV3
                property={properties.value as ProxyProperty<FormFieldDescriptorInput>}
                field={properties.field}
                userGroupsIds={userGroupsIds}
                canAddUserGroups={false}
            />
        </Confirm>
    );

    return {
        onNew: () => {
            properties.field.setValue(Object.create(null));
            properties.value.setValue(Object.create(null));
            setShow(true);
        },
        element,
        onSetEdit: (value: FormFieldDescriptorInput, field: FormField) => {
            properties.field.setValue(field);
            properties.value.setValue(value);
            setShow(true);
        },
    };
}

export function useNewFieldDrawer({
    onSave,
}: {
    onSave: (field: FormField) => void;
}) {
    const [saveFormField] = useSaveFormFieldMutation();

    const [formField, setFormField] = useState<FormField | undefined>();

    const isOptions = formField?.type
                && CHOICE_FIELDS.has(formField.type);

    const isEdit = formField?.id != null;
    const { element, formTracker, setOpen } = useDrawerForm({
        title: isEdit ? "Edit Field" : "Add Field",
        className: Styles.drawer,
        formTrackerBuilder: {
            handler: builder => builder
                .proxy("value", () => ({
                    fieldId: formField?.id,
                } as FormFieldDescriptorInput | undefined))
                .proxy<"field", FormField | undefined>("field", () => formField),
            dependencies: [useDependencyComparison(formField, createDeepEqual(DEEP_COMPARER))],
        },
        body: ({ properties }) => (
            <CustomFieldV3
                property={properties.value as ProxyProperty<FormFieldDescriptorInput>}
                field={properties.field}
                canOverride={false}
                isEdit={isEdit}
                userGroupsIds={properties.field.value?.userGroupIds as string[] | undefined}
                canAddUserGroups
            />
        ),
        onHide: () => {
            setFormField(undefined);
        },
        size: isOptions ? "xlarge" : "small",
        onSave: () => {
            const { properties } = formTracker;
            if (formTracker.properties.field.value) {
                LoggingHandler.instance.wrap(async () => {
                    const formFieldValue = properties.field.serialize() as FormField;
                    const { id } = formFieldValue;
                    delete formFieldValue.id;
                    formFieldValue.visibility = FormFieldVisibility.Reusable;
                    const saved = await saveFormField({
                        refetchQueries: [
                            { query: GetFormDataDocument },
                            {
                                query: GetRequestTypesDocument,
                                variables: {
                                    type: FormType.Default,
                                    shouldIncludeOptions: true,
                                },
                            },
                        ],
                        variables: {
                            id,
                            formField: cloneWithoutTypename(formFieldValue),
                        },
                    });
                    onSave(saved.data?.saveFormField as FormField);
                });
            }
            setFormField(undefined);
            setOpen(false);
        },
    });

    formTracker.useOnChange(() => {
        setFormField(formTracker.properties.field.value);
    });

    return {
        element,
        formTracker,
        setNew: () => {
            setFormField(undefined);
            setOpen(true);
        },
        setEdit: (value: FormField) => {
            setFormField(value);
            setOpen(true);
        },
    };
}
